/**
 * Main Landing Page for closedloopdiving.com
 *
 * This is the primary landing page for the main domain (closedloopdiving.com).
 * It mentions online training and the upcoming SF2 CCR Checklist & Dive Log app.
 * The training platform lives at training.closedloopdiving.com.
 */

import { Link } from 'react-router-dom';
import { Logo } from '@/components/Logo';

export function MainLanding() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--cld-color-off-white)',
      }}
    >
      {/* Header */}
      <header
        style={{
          backgroundColor: 'var(--cld-color-white)',
          borderBottom: '1px solid var(--cld-color-gray)',
          padding: 'var(--cld-spacing-md) var(--cld-spacing-xl)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Logo size="medium" />
        <nav style={{ display: 'flex', gap: 'var(--cld-spacing-lg)' }}>
          <Link
            to="/about"
            style={{
              color: 'var(--cld-color-gray)',
              textDecoration: 'none',
              fontWeight: 'var(--cld-font-weight-medium)',
            }}
          >
            About
          </Link>
          <Link
            to="/terms"
            style={{
              color: 'var(--cld-color-gray)',
              textDecoration: 'none',
              fontWeight: 'var(--cld-font-weight-medium)',
            }}
          >
            Terms
          </Link>
        </nav>
      </header>

      {/* Hero Section */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--cld-spacing-3xl) var(--cld-spacing-xl)',
          textAlign: 'center',
        }}
      >
        {/* Main Content */}
        <div style={{ maxWidth: '800px' }}>
          {/* Hero Graphic */}
          <div
            style={{
              width: '120px',
              height: '120px',
              borderRadius: 'var(--cld-radius-full)',
              background: 'linear-gradient(135deg, var(--cld-color-primary) 0%, var(--cld-color-secondary) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--cld-spacing-xl)',
              boxShadow: 'var(--cld-shadow-lg)',
            }}
          >
            <span style={{ fontSize: '4rem' }}>🤿</span>
          </div>

          {/* Headline */}
          <h1
            style={{
              color: 'var(--cld-color-primary)',
              fontFamily: 'var(--cld-font-display)',
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 'var(--cld-font-weight-bold)',
              marginBottom: 'var(--cld-spacing-md)',
              lineHeight: 1.2,
            }}
          >
            Technical Diving Education
          </h1>

          <p
            style={{
              color: 'var(--cld-color-gray)',
              fontSize: 'var(--cld-font-size-lg)',
              lineHeight: 1.6,
              marginBottom: 'var(--cld-spacing-lg)',
              maxWidth: '600px',
              margin: '0 auto var(--cld-spacing-lg)',
            }}
          >
            ClosedLoopDiving provides supplementary online training tools for technical divers.
            Reinforce the skills you learn in formal instructor-led courses with our AI-powered
            learning platform.
          </p>

          {/* Training Aid Disclaimer */}
          <div
            style={{
              backgroundColor: 'rgba(2, 48, 71, 0.05)',
              border: '1px solid var(--cld-color-gray)',
              borderRadius: 'var(--cld-radius-md)',
              padding: 'var(--cld-spacing-md)',
              maxWidth: '600px',
              margin: '0 auto var(--cld-spacing-xl)',
              textAlign: 'left',
            }}
          >
            <p
              style={{
                fontSize: 'var(--cld-font-size-sm)',
                color: 'var(--cld-color-gray)',
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              <strong style={{ color: 'var(--cld-color-primary)' }}>Training Aid Only:</strong>{' '}
              Our courses are supplementary practice for students in supervised training programs.
              We do not certify or license divers for decompression or CCR dives.
              Certification must be obtained through a licensed training agency.{' '}
              <Link to="/terms" style={{ color: 'var(--cld-color-secondary)' }}>
                Learn more
              </Link>
            </p>
          </div>

          {/* Primary CTA */}
          <a
            href="https://training.closedloopdiving.com"
            style={{
              display: 'inline-block',
              padding: 'var(--cld-spacing-md) var(--cld-spacing-2xl)',
              backgroundColor: 'var(--cld-color-accent)',
              color: 'var(--cld-color-white)',
              borderRadius: 'var(--cld-radius-md)',
              textDecoration: 'none',
              fontFamily: 'var(--cld-font-display)',
              fontWeight: 'var(--cld-font-weight-bold)',
              fontSize: 'var(--cld-font-size-lg)',
              boxShadow: 'var(--cld-shadow-md)',
              transition: 'all var(--cld-transition-fast)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--cld-color-primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--cld-color-accent)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Start Learning
          </a>

          {/* Divider */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--cld-spacing-md)',
              margin: 'var(--cld-spacing-3xl) 0',
            }}
          >
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--cld-color-gray)' }} />
            <span style={{ color: 'var(--cld-color-gray)', fontSize: 'var(--cld-font-size-sm)' }}>
              COMING SOON
            </span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--cld-color-gray)' }} />
          </div>

          {/* Coming Soon Section */}
          <div
            style={{
              backgroundColor: 'var(--cld-color-white)',
              borderRadius: 'var(--cld-radius-lg)',
              padding: 'var(--cld-spacing-xl)',
              boxShadow: 'var(--cld-shadow-md)',
              border: '1px solid var(--cld-color-gray)',
            }}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: 'var(--cld-radius-md)',
                backgroundColor: 'rgba(33, 158, 188, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto var(--cld-spacing-md)',
              }}
            >
              <span style={{ fontSize: '2rem' }}>📋</span>
            </div>

            <h2
              style={{
                color: 'var(--cld-color-primary)',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-xl)',
                fontWeight: 'var(--cld-font-weight-bold)',
                marginBottom: 'var(--cld-spacing-sm)',
              }}
            >
              SF2 CCR Checklist & Dive Log
            </h2>

            <p
              style={{
                color: 'var(--cld-color-gray)',
                lineHeight: 1.6,
                marginBottom: 'var(--cld-spacing-md)',
              }}
            >
              We're developing a mobile app specifically for ScubaForce SF2 rebreather divers.
              Pre-dive checklists, dive logging, and analysis tools designed for CCR operations.
            </p>

            <div
              style={{
                display: 'inline-block',
                padding: 'var(--cld-spacing-xs) var(--cld-spacing-md)',
                backgroundColor: 'rgba(33, 158, 188, 0.1)',
                color: 'var(--cld-color-secondary)',
                borderRadius: 'var(--cld-radius-sm)',
                fontSize: 'var(--cld-font-size-sm)',
                fontWeight: 'var(--cld-font-weight-medium)',
              }}
            >
              In Development
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer
        style={{
          backgroundColor: 'var(--cld-color-primary)',
          color: 'var(--cld-color-white)',
          padding: 'var(--cld-spacing-xl)',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <p style={{ marginBottom: 'var(--cld-spacing-sm)', opacity: 0.9 }}>
            ClosedLoopDiving provides supplementary educational tools only.
            Not a substitute for formal instructor-led training.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--cld-spacing-lg)', flexWrap: 'wrap' }}>
            <Link
              to="/terms"
              style={{
                color: 'var(--cld-color-white)',
                opacity: 0.7,
                textDecoration: 'none',
                fontSize: 'var(--cld-font-size-sm)',
              }}
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              style={{
                color: 'var(--cld-color-white)',
                opacity: 0.7,
                textDecoration: 'none',
                fontSize: 'var(--cld-font-size-sm)',
              }}
            >
              Privacy
            </Link>
            <Link
              to="/about"
              style={{
                color: 'var(--cld-color-white)',
                opacity: 0.7,
                textDecoration: 'none',
                fontSize: 'var(--cld-font-size-sm)',
              }}
            >
              About
            </Link>
            <Link
              to="/instructor/register"
              style={{
                color: 'var(--cld-color-white)',
                opacity: 0.7,
                textDecoration: 'none',
                fontSize: 'var(--cld-font-size-sm)',
              }}
            >
              Instructors
            </Link>
            <a
              href="mailto:info@closedloopdiving.com"
              style={{
                color: 'var(--cld-color-white)',
                opacity: 0.7,
                textDecoration: 'none',
                fontSize: 'var(--cld-font-size-sm)',
              }}
            >
              Contact
            </a>
          </div>
          <p
            style={{
              marginTop: 'var(--cld-spacing-md)',
              fontSize: 'var(--cld-font-size-sm)',
              opacity: 0.5,
            }}
          >
            &copy; {new Date().getFullYear()} ClosedLoopDiving. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default MainLanding;
